import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'
import { Avancement } from '@/pages/onboarding/MiseEnService'
import { ApercuEquipe } from '@/pages/onboarding/Apercus'
import { inviteAgent } from '@/api/auth'
import { messageErreurInvitation } from '@/api/team'
import { defaultLocale } from '@/lib/locales'

const COURRIEL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Les morceaux du champ : séparés par virgule, point-virgule, espace ou retour à la ligne. */
const morceaux = (texte: string) =>
  texte
    .split(/[\s,;]+/)
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean)

export function OnboardingTeamPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Un seul champ, plusieurs adresses séparées par des virgules : la liste de
  // droite se remplit pendant qu'on tape. Pas de bouton « Ajouter », donc pas
  // d'invitation perdue parce qu'on a cliqué directement sur Continuer.
  const [texte, setTexte] = useState('')
  const [quitte, setQuitte] = useState(false)
  const tous = morceaux(texte)
  const emails = [...new Set(tous.filter((m) => COURRIEL.test(m)))]
  // Le dernier morceau peut être en cours de frappe : on ne le signale qu'en quittant le champ.
  const enCours = /[\s,;]$/.test(texte) || quitte ? '' : tous[tous.length - 1]
  const invalides = tous.filter((m) => !COURRIEL.test(m) && m !== enCours)

  const removeEmail = (email: string) =>
    setTexte(
      morceaux(texte)
        .filter((m) => m !== email)
        .join(', '),
    )

  const sendMutation = useMutation({
    // Chaque email vit sa vie. L'ancien Promise.all rejetait TOUT au premier
    // refus : une invitation déjà partie chez son destinataire était annoncée
    // comme un échec, et le manager risquait de la renvoyer — ou de rester
    // bloqué sur la page alors qu'une partie du travail avait réussi.
    mutationFn: async (toInvite: string[]) => {
      const resultats = await Promise.allSettled(
        toInvite.map((email) => inviteAgent(email)),
      )
      const envoyes: string[] = []
      const refus: { email: string; raison: string }[] = []
      resultats.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          envoyes.push(toInvite[i])
          return
        }
        console.error('[OnboardingTeam]', toInvite[i], r.reason)
        // Un 402 arrive ici aussi : l'entreprise peut n'avoir aucun
        // abonnement à ce stade du parcours. Dire lequel des obstacles
        // bloque vaut mieux qu'un « impossible d'envoyer », qui laisse
        // chercher.
        const data = (
          r.reason as {
            response?: { data?: { error?: string; message?: string } }
          }
        )?.response?.data
        refus.push({ email: toInvite[i], raison: messageErreurInvitation(data, t) })
      })
      return { envoyes, refus }
    },
    onSuccess: ({ envoyes, refus }) => {
      envoyes.forEach((email) =>
        toast.success(t('manager.team.invite.success', { email })),
      )
      if (refus.length === 0) {
        navigate(`/${locale}/onboarding/mobile`)
        return
      }
      refus.forEach(({ email, raison }) => toast.error(`${email} : ${raison}`))
      // Ne garder à l'écran que ce qui reste à régler : les envoyées sont
      // faites, les rejouer ne ferait que produire de nouveaux refus.
      setTexte(refus.map(({ email }) => email).join(', '))
    },
  })

  const aInviter = emails

  const handleSend = () => {
    if (aInviter.length === 0) {
      navigate(`/${locale}/onboarding/mobile`)
      return
    }
    sendMutation.mutate(aInviter)
  }

  const sending = sendMutation.isPending

  return (
    <OnboardingNavyShell
      deconnexion={false}
      entete={<Avancement etape={6} />}
      panneau={<ApercuEquipe locale={locale} emails={emails} onRetirer={removeEmail} />}
      panneauInteractif
    >
      <main className="flex-grow flex items-center justify-center px-5 py-6 md:py-10">
        <div className="w-full max-w-[500px]">
          <div className="text-left mb-10">
            <h1 className="text-[28px] md:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] text-balance mb-4">
              {t('onboarding.team.title')}
            </h1>
          </div>

          <div className="space-y-8">
            <div className="flex flex-col gap-3">
              <label
                htmlFor="courriels-equipe"
                className="text-sm font-medium"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                {t('onboarding.team.emailLabel')}
              </label>
              <textarea
                id="courriels-equipe"
                value={texte}
                onChange={(e) => {
                  setTexte(e.target.value)
                  setQuitte(false)
                }}
                onBlur={() => setQuitte(true)}
                rows={3}
                placeholder={t('onboarding.team.emailPlaceholder')}
                className="w-full px-4 py-3 rounded-xl outline-none transition-all resize-none text-[15px] leading-relaxed focus:border-white/40"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', color: 'white' }}
              />
              {invalides.length > 0 && (
                <p className="text-[13px]" style={{ color: '#FCA5A5' }}>
                  {t('onboarding.team.invalid', { list: invalides.join(', ') })}
                </p>
              )}
            </div>

            {/* Sur téléphone, pas de liste à droite : on montre combien partiront. */}
            {emails.length > 0 && (
              <p className="text-[13px] lg:hidden" style={{ color: '#8B9BB4' }}>
                {t('onboarding.team.count', { count: emails.length })}
              </p>
            )}

            <div className="mt-14 pt-6 border-t border-white/[0.06] flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              {/* Retour en bas à gauche, sur la ligne du bouton principal. */}
              <button
                type="button"
                onClick={() => navigate(`/${locale}/onboarding/urgence`)}
                className="h-11 sm:-ml-2 px-2 text-[14px] font-medium inline-flex items-center justify-center gap-1.5 rounded-lg hover:text-white transition-colors"
                style={{ color: '#8B9BB4' }}
              >
                <ArrowLeft className="w-4 h-4" />
                {t('onboarding.miseEnService.back')}
              </button>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:gap-4">
                {/* « Passer » seulement si des adresses attendent : sinon le bouton dit déjà « Continuer ». */}
                {emails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(`/${locale}/onboarding/mobile`)}
                    className="h-11 sm:h-auto text-[14px] font-medium whitespace-nowrap transition-colors hover:text-white"
                    style={{ color: '#8B9BB4' }}
                  >
                    {t('onboarding.team.skipButton')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="w-full sm:w-auto h-11 px-7 rounded-xl text-[15px] font-semibold whitespace-nowrap transition-all hover:bg-white/90 active:scale-[0.98] disabled:opacity-35"
                  style={{ backgroundColor: 'white', color: '#0A192F' }}
                >
                  {sending
                    ? t('onboarding.team.sending')
                    : aInviter.length > 0
                      ? t('onboarding.team.sendButton')
                      : t('onboarding.team.continueButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </OnboardingNavyShell>
  )
}
